/**
 * Reading just enough of a bank's CSV to let someone say what its columns are.
 *
 * The API reads the file and states nothing is guessed, so what's here only fills in a form the person
 * can see and change. A date format is filled in only when the first date can be read one way.
 */

/** Splits rows, honouring quotes. Stops at `limit` rows, because this is a preview. */
export function parseCsv(text: string, delimiter: string, limit = 6): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let quoted = false;

  for (let i = 0; i < text.length && rows.length < limit; i++) {
    const char = text[i]!;
    if (quoted) {
      if (char === '"' && text[i + 1] === '"') {
        cell += '"';
        i++;
      } else if (char === '"') {
        quoted = false;
      } else {
        cell += char;
      }
    } else if (char === '"') {
      quoted = true;
    } else if (char === delimiter) {
      row.push(cell);
      cell = "";
    } else if (char === "\n" || char === "\r") {
      if (char === "\r" && text[i + 1] === "\n") i++;
      row.push(cell);
      cell = "";
      if (row.some((value) => value.trim() !== "")) rows.push(row);
      row = [];
    } else {
      cell += char;
    }
  }

  if (rows.length < limit && (cell !== "" || row.length > 0)) {
    row.push(cell);
    if (row.some((value) => value.trim() !== "")) rows.push(row);
  }
  return rows;
}

export interface CsvColumns {
  date: number | null;
  description: number | null;
  amount: number | null;
  debit: number | null;
  credit: number | null;
  reference: number | null;
  balance: number | null;
}

const find = (headers: string[], pattern: RegExp) => {
  const index = headers.findIndex((header) => pattern.test(header.trim().toLowerCase()));
  return index === -1 ? null : index;
};

/** Which column looks like which, from its heading. */
export function guessColumns(headers: string[]): CsvColumns {
  return {
    date: find(headers, /date/),
    description: find(headers, /descr|narrat|particular|details|remark/),
    amount: find(headers, /^amount|^amt/),
    debit: find(headers, /debit|withdraw|^dr\b/),
    credit: find(headers, /credit|deposit|^cr\b/),
    reference: find(headers, /ref|cheque|chq/),
    balance: find(headers, /balance|^bal\b/),
  };
}

export const DATE_FORMATS = ["yyyy-MM-dd", "dd/MM/yyyy", "MM/dd/yyyy", "dd-MM-yyyy", "dd MMM yyyy"];

/**
 * A format only when the value can be read one way. In 01/02/2026 the day and month could be either, so
 * that's left for the person to say: the two readings are a month apart.
 */
export function guessDateFormat(value: string): string {
  const text = value.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) return "yyyy-MM-dd";
  if (/^\d{2} [A-Za-z]{3} \d{4}$/.test(text)) return "dd MMM yyyy";
  if (/^\d{2}-\d{2}-\d{4}$/.test(text) && Number(text.slice(0, 2)) > 12) return "dd-MM-yyyy";

  const slashed = /^(\d{2})\/(\d{2})\/\d{4}$/.exec(text);
  if (slashed) {
    if (Number(slashed[1]) > 12) return "dd/MM/yyyy";
    if (Number(slashed[2]) > 12) return "MM/dd/yyyy";
  }
  return "";
}
